import { useEffect, useState } from 'react';

const KEY = 'learnleaf.manage-licenses.columns.v1';

interface ColumnTogglesStore {
  hidden: string[];
}

function read(): ColumnTogglesStore {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { hidden: [] };
    const parsed = JSON.parse(raw) as Partial<ColumnTogglesStore>;
    if (!Array.isArray(parsed.hidden)) return { hidden: [] };
    return { hidden: parsed.hidden };
  } catch {
    return { hidden: [] };
  }
}

function write(store: ColumnTogglesStore) {
  localStorage.setItem(KEY, JSON.stringify(store));
}

export function useLicenseColumnToggles() {
  const [hidden, setHidden] = useState<string[]>(() => read().hidden);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === KEY) setHidden(read().hidden);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const isHidden = (columnKey: string) => hidden.includes(columnKey);

  const toggleColumn = (columnKey: string) => {
    const next = hidden.includes(columnKey)
      ? hidden.filter(k => k !== columnKey)
      : [...hidden, columnKey];
    write({ hidden: next });
    setHidden(next);
  };

  const showAll = () => {
    write({ hidden: [] });
    setHidden([]);
  };

  const hideAll = (columnKeys: string[]) => {
    write({ hidden: columnKeys });
    setHidden(columnKeys);
  };

  return { hidden, isHidden, toggleColumn, showAll, hideAll };
}
